import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, AlertTriangle } from 'lucide-react';
import { createSurvey, updateSurvey } from '../../../services/api/survey.service';
import type {
  Survey, SurveyInput, SurveyQuestion, SurveyOption, QuestionType, SurveyAudience,
} from '../../../types/survey';

interface Props {
  initial?: Survey;
  onSaved: () => void;
  onCancel: () => void;
}

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'multi', label: 'Multiple choice (select many)' },
  { value: 'single', label: 'Single choice (select one)' },
  { value: 'rating', label: 'Rating (1–5)' },
  { value: 'nps', label: 'NPS (0–10)' },
  { value: 'text', label: 'Free text' },
];

const AUDIENCES: { value: SurveyAudience; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'owners', label: 'Artist owners' },
  { value: 'team_members', label: 'Team members' },
];

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50);

const emptyQuestion = (): SurveyQuestion => ({
  question_text: '',
  question_type: 'multi',
  options: [],
  is_required: true,
});

const isChoice = (t: QuestionType) => t === 'single' || t === 'multi';

const SurveyBuilder: React.FC<Props> = ({ initial, onSaved, onCancel }) => {
  const editing = !!initial;
  const hasResponses = (initial?.response_count || 0) > 0;

  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [audience, setAudience] = useState<SurveyAudience>(initial?.audience || 'all');
  const [allowMultiple, setAllowMultiple] = useState(initial?.allow_multiple || false);
  const [isAnonymous, setIsAnonymous] = useState(initial?.is_anonymous || false);
  const [questions, setQuestions] = useState<SurveyQuestion[]>(
    initial?.questions?.length ? initial.questions.map((q) => ({ ...q, options: q.options || [] })) : [emptyQuestion()],
  );
  const [saving, setSaving] = useState(false);

  const updateQuestion = (idx: number, patch: Partial<SurveyQuestion>) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);
  const removeQuestion = (idx: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const addOption = (qIdx: number) => {
    setQuestions((prev) => prev.map((q, i) =>
      i === qIdx ? { ...q, options: [...q.options, { key: '', label: '', description: '' }] } : q));
  };
  const updateOption = (qIdx: number, oIdx: number, patch: Partial<SurveyOption>) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const options = q.options.map((o, j) => {
        if (j !== oIdx) return o;
        const next = { ...o, ...patch };
        // auto-derive key from label unless the user has hand-edited the key
        if (patch.label !== undefined && (!o.key || o.key === slugify(o.label))) {
          next.key = slugify(patch.label);
        }
        return next;
      });
      return { ...q, options };
    }));
  };
  const removeOption = (qIdx: number, oIdx: number) => {
    setQuestions((prev) => prev.map((q, i) =>
      i === qIdx ? { ...q, options: q.options.filter((_, j) => j !== oIdx) } : q));
  };

  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required';
    if (questions.length === 0) return 'Add at least one question';
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) return `Question ${i + 1}: text is required`;
      if (isChoice(q.question_type)) {
        if (q.options.length === 0) return `Question ${i + 1}: add at least one option`;
        const keys = new Set<string>();
        for (const o of q.options) {
          if (!o.label.trim()) return `Question ${i + 1}: every option needs a label`;
          const key = o.key || slugify(o.label);
          if (!key) return `Question ${i + 1}: invalid option key`;
          if (keys.has(key)) return `Question ${i + 1}: duplicate option key "${key}"`;
          keys.add(key);
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    const normalizedQuestions: SurveyQuestion[] = questions.map((q, i) => ({
      ...q,
      display_order: i + 1,
      options: isChoice(q.question_type)
        ? q.options.map((o) => ({ key: o.key || slugify(o.label), label: o.label.trim(), description: o.description?.trim() || undefined }))
        : [],
    }));

    const payload: SurveyInput = {
      title: title.trim(),
      description: description.trim() || null,
      audience,
      allow_multiple: allowMultiple,
      is_anonymous: isAnonymous,
      questions: normalizedQuestions,
    };

    try {
      setSaving(true);
      if (editing && initial) {
        // Don't send questions if responses exist (backend rejects it; keep aggregation stable)
        const update: Partial<SurveyInput> = hasResponses
          ? { title: payload.title, description: payload.description, audience, allow_multiple: allowMultiple, is_anonymous: isAnonymous }
          : payload;
        await updateSurvey(initial.id, update);
        toast.success('Survey updated');
      } else {
        await createSurvey(payload);
        toast.success('Survey created (draft)');
      }
      onSaved();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button onClick={onCancel} className="flex items-center gap-1 text-[0.75rem] text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={14} /> Back to surveys
      </button>

      <h1 className="text-lg font-semibold text-gray-900 mb-1">
        {editing ? 'Edit survey' : 'New survey'}
      </h1>
      <p className="text-[0.75rem] text-gray-500 mb-6">
        {editing ? 'Update details below.' : 'Create a survey as a draft, then publish it when ready.'}
      </p>

      {editing && hasResponses && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-[0.72rem] text-amber-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>This survey already has responses. Questions are locked to keep results consistent — you can still edit the title, description, audience and settings.</span>
        </div>
      )}

      {/* Meta */}
      <div className="space-y-4 mb-8">
        <div>
          <label className="block text-[0.72rem] font-medium text-gray-700 mb-1">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Feature Prioritization"
            className="w-full text-[0.8rem] border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
          />
        </div>
        <div>
          <label className="block text-[0.72rem] font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional intro shown above the questions"
            className="w-full text-[0.8rem] border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[0.72rem] font-medium text-gray-700 mb-1">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value as SurveyAudience)}
              className="w-full text-[0.8rem] border border-gray-200 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-black"
            >
              {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-[0.72rem] text-gray-700 sm:mt-6">
            <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} />
            Allow multiple responses
          </label>
          <label className="flex items-center gap-2 text-[0.72rem] text-gray-700 sm:mt-6">
            <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />
            Anonymous results
          </label>
        </div>
      </div>

      {/* Questions */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">Questions</h2>
        {!hasResponses && (
          <button onClick={addQuestion} className="flex items-center gap-1 text-[0.72rem] text-gray-700 hover:text-black">
            <Plus size={13} /> Add question
          </button>
        )}
      </div>

      <div className="space-y-4">
        {questions.map((q, qIdx) => (
          <div key={qIdx} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <GripVertical size={15} className="text-gray-300 mt-2" />
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[0.7rem] text-gray-400 font-medium w-5">{qIdx + 1}.</span>
                  <input
                    value={q.question_text}
                    onChange={(e) => updateQuestion(qIdx, { question_text: e.target.value })}
                    placeholder="Question text"
                    disabled={hasResponses}
                    className="flex-1 text-[0.8rem] border border-gray-200 rounded-lg px-3 py-1.5 disabled:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-black"
                  />
                </div>
                <div className="flex items-center gap-3 pl-7">
                  <select
                    value={q.question_type}
                    onChange={(e) => updateQuestion(qIdx, {
                      question_type: e.target.value as QuestionType,
                      options: isChoice(e.target.value as QuestionType) ? q.options : [],
                    })}
                    disabled={hasResponses}
                    className="text-[0.72rem] border border-gray-200 rounded-lg px-2 py-1.5 bg-white disabled:bg-gray-50"
                  >
                    {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-[0.72rem] text-gray-600">
                    <input
                      type="checkbox"
                      checked={q.is_required}
                      disabled={hasResponses}
                      onChange={(e) => updateQuestion(qIdx, { is_required: e.target.checked })}
                    />
                    Required
                  </label>
                  {!hasResponses && questions.length > 1 && (
                    <button onClick={() => removeQuestion(qIdx)} className="ml-auto text-red-500 hover:text-red-700">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {isChoice(q.question_type) && (
                  <div className="pl-7 space-y-2">
                    {q.options.map((o, oIdx) => (
                      <div key={oIdx} className="flex items-start gap-2">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            value={o.label}
                            onChange={(e) => updateOption(qIdx, oIdx, { label: e.target.value })}
                            placeholder="Option label"
                            disabled={hasResponses}
                            className="text-[0.75rem] border border-gray-200 rounded px-2 py-1.5 disabled:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-black"
                          />
                          <input
                            value={o.description || ''}
                            onChange={(e) => updateOption(qIdx, oIdx, { description: e.target.value })}
                            placeholder="Description (optional)"
                            disabled={hasResponses}
                            className="text-[0.75rem] border border-gray-200 rounded px-2 py-1.5 disabled:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-black"
                          />
                        </div>
                        {!hasResponses && (
                          <button onClick={() => removeOption(qIdx, oIdx)} className="text-gray-400 hover:text-red-600 mt-1.5">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                    {!hasResponses && (
                      <button onClick={() => addOption(qIdx)} className="flex items-center gap-1 text-[0.7rem] text-gray-500 hover:text-black">
                        <Plus size={12} /> Add option
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 mt-8">
        <button onClick={onCancel} className="text-[0.75rem] px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 text-[0.75rem] px-4 py-2 rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
        >
          <Save size={14} /> {saving ? 'Saving…' : editing ? 'Save changes' : 'Create survey'}
        </button>
      </div>
    </div>
  );
};

export default SurveyBuilder;
